"use client";

import { motion } from "framer-motion";
import kitchenImg from "@/assets/showcase-kitchen.jpg";
import officeImg from "@/assets/showcase-office.jpg";
import bedroomImg from "@/assets/showcase-bedroom.jpg";
import { ArrowRight } from "lucide-react";

const SHOWCASE = [
  {
    img: kitchenImg,
    eyebrow: "Ambient",
    title: "Light that knows the time of day.",
    body:
      "Apollo learns the rhythm of your home and tunes brightness, color, and warmth to match. Mornings feel brighter. Evenings feel calmer.",
  },
  {
    img: officeImg,
    eyebrow: "Focus",
    title: "Quiet, until you need it.",
    body:
      "Sub-3W idle, far-field microphones, and on-device intelligence. Apollo only listens when you say so — and never sends raw audio to the cloud.",
  },
  {
    img: bedroomImg,
    eyebrow: "Rest",
    title: "Drift off. Wake up gently.",
    body:
      "Pair Apollo with your bedroom routines for adaptive lighting, white noise, and a sunrise alarm that fades the room back to life.",
  },
];

export function Showcase() {
  const scrollToSection = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  const textVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.8,
        ease: [0.21, 0.47, 0.32, 0.98],
      },
    },
  };

  const textVariantsReverse = {
    hidden: { opacity: 0, x: 20 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.8,
        ease: [0.21, 0.47, 0.32, 0.98],
      },
    },
  };

  const imageVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 1,
        ease: [0.21, 0.47, 0.32, 0.98],
      },
    },
  };

  return (
    <section id="showcase" className="py-32 lg:py-40 overflow-hidden">
      <div className="mx-auto max-w-7xl px-6 lg:px-10 space-y-32 lg:space-y-40">
        {SHOWCASE.map((row, i) => {
          const reverse = i % 2 === 1;
          return (
            <div
              key={row.title}
              className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center"
            >
              <motion.div 
                className={reverse ? "lg:order-2" : ""}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={imageVariants}
              >
                <div className="overflow-hidden rounded-3xl bg-muted">
                  <img
                    src={row.img.src}
                    alt={row.title}
                    width={1400}
                    height={1100}
                    loading="lazy"
                    className="h-full w-full object-cover aspect-[5/4]"
                  />
                </div>
              </motion.div>
              
              <motion.div 
                className={reverse ? "lg:order-1" : ""}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={reverse ? textVariantsReverse : textVariants}
              >
                <p className="text-xs uppercase tracking-[0.25em] text-primary mb-5">
                  {row.eyebrow}
                </p>
                <h2 className="text-4xl lg:text-5xl font-semibold leading-tight tracking-tight text-foreground">
                  {row.title}
                </h2>
                <p className="mt-6 text-lg text-muted-foreground leading-relaxed max-w-lg">
                  {row.body}
                </p>
                <a
                  href="#pricing"
                  onClick={(e) => scrollToSection(e, "pricing")}
                  className="mt-8 inline-flex items-center gap-2 text-sm font-medium text-foreground border-b border-foreground/30 pb-1 hover:border-foreground transition"
                >
                  Learn more <ArrowRight className="h-4 w-4" />
                </a>
              </motion.div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
